import { PlayerDetailsResponseDto } from "@/lib/api/interfaces/players.interface";
import {
  usePlayersApplyImageAssetEdit,
  usePlayersDelete,
  usePlayersDeleteImageAsset,
  usePlayersFindOneQuery as usePlayersFindOne,
  usePlayersPreviewBackgroundRemoval,
  usePlayersRestoreOriginalImageAsset,
  usePlayersUpdate,
  usePlayersUploadImageAsset,
} from "@/lib/api/requests/players.requests";
import { toast } from "@/lib/toast";
import { formatDateForForm, formatDateWithoutTime } from "@/lib/utils/date-formatter";
import { cn } from "@/lib/utils/cn";
import { formatTextWithLineBreaks } from "@/lib/utils/text";
import { PLAYER_POSITIONS, translatePosition } from "@/lib/utils/player-utils";
import { Badge } from "@/ui-components/ui/badge";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { MultiSelect } from "@/ui-components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Textarea } from "@/ui-components/ui/textarea";
import {
  DetailsBaseDialog,
  DetailsBaseDialogMode,
  DialogContentItem,
  DialogContentSection,
  DialogHeaderSection,
  DialogMetaSection,
  DialogTextSection,
  DialogFormSection,
} from "@/util-components/dialog/base-dialogs";
import { ManagedImageBusyAction, ManagedImageField, persistManagedImageDraft, ManagedImageFieldSkeleton } from "@/util-components/media";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  BookOpen,
  Calendar,
  Flag,
  Image as ImageIcon,
  Quote,
  Radio,
  Scale,
  Users,
  User,
  Hash,
  MapPin,
  IdCardLanyard,
  LibraryBig,
  Plus,
  Camera,
} from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { parseTeamsToSelectOptions } from "./create-dialog";

type PlayerImageSelection = { type: "existing"; id: string } | { type: "new" };

const PlayerImageGalleryItem = ({
  previewUrl,
  isActive,
  isAddTile = false,
  onClick,
}: {
  previewUrl?: string | null;
  isActive: boolean;
  isAddTile?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "group flex min-w-[64px] flex-col items-center gap-1.5 transition-opacity",
      isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
    )}
  >
    <div
      className={cn(
        "flex size-16 items-center justify-center overflow-hidden rounded-xl border bg-white transition-all",
        isActive ? "border-blue-500 ring-4 ring-blue-500/10" : "border-gray-200 hover:border-gray-300",
        isAddTile && "border-dashed bg-gray-50"
      )}
    >
      {previewUrl ? (
        <img src={previewUrl} alt="" className="h-full w-full object-cover object-top" />
      ) : isAddTile ? (
        <Plus className={cn("size-4", isActive ? "text-blue-600" : "text-gray-400")} />
      ) : (
        <ImageIcon className="size-5 text-gray-300" />
      )}
    </div>
  </button>
);

const getInitials = (firstName?: string | null, lastName?: string | null) => {
  const first = (firstName ?? "").trim();
  const last = (lastName ?? "").trim();
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase() || "SP";
};

const getAgeFromBirthDate = (birthDate?: string | null) => {
  if (!birthDate) return null;

  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDelta = now.getMonth() - date.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const ProfileAvatar = ({
  imageUrl,
  firstName,
  lastName,
  size = "md",
}: {
  imageUrl?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: "sm" | "md";
}) => {
  const sizeClass = size === "sm" ? "size-14" : "size-20";

  return (
    <div className={cn("relative shrink-0 overflow-hidden rounded-full border bg-background shadow-sm", sizeClass)}>
      {imageUrl ? (
        <img src={imageUrl} alt={`${firstName ?? ""} ${lastName ?? ""}`.trim()} className="h-full w-full object-cover object-top" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted text-sm font-semibold text-muted-foreground">
          {getInitials(firstName, lastName)}
        </div>
      )}
    </div>
  );
};

const PlayerProfileHeader = ({
  player,
  rightElement,
}: {
  player: Pick<
    PlayerDetailsResponseDto,
    | "firstName"
    | "lastName"
    | "birthDate"
    | "imageUrl"
    | "thumbnailUrl"
    | "jerseyNumber"
    | "position"
    | "teamTitle"
    | "birthDateConsent"
    | "imageState"
  >;
  rightElement?: ReactNode;
}) => {
  const age = useMemo(() => getAgeFromBirthDate(player.birthDate), [player.birthDate]);
  const metaParts = [
    player.jerseyNumber ? `#${player.jerseyNumber}` : null,
    player.position ? translatePosition(player.position) : null,
    typeof age === "number" ? `${age} Jahre` : null,
  ].filter(Boolean);

  const avatarUrl = player.imageState?.active?.thumbnailUrl || player.thumbnailUrl || player.imageUrl;

  return (
    <DialogHeaderSection className="p-6">
      <div className="flex items-start gap-5">
        <ProfileAvatar imageUrl={avatarUrl} firstName={player.firstName} lastName={player.lastName} />

        <div className="min-w-0 flex-1 pt-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-2xl font-black tracking-tight text-white">
                {player.firstName} {player.lastName}
              </h2>
              <p className="mt-1.5 text-xs font-bold uppercase tracking-widest text-white/70">
                {metaParts.length ? metaParts.join(" • ") : "Spieler"}
              </p>
            </div>
            {rightElement}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-full border-white/10 bg-white/10 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              {player.teamTitle || "Keine Mannschaft"}
            </Badge>
            <Badge
              variant="secondary"
              className={cn(
                "rounded-full px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                player.birthDateConsent
                  ? "border-emerald-400/20 bg-emerald-400/20 text-emerald-100"
                  : "border-white/10 bg-white/10 text-white/60"
              )}
            >
              {player.birthDateConsent ? "Geburtstagsposts: erlaubt" : "Geburtstagsposts: nicht erlaubt"}
            </Badge>
          </div>
        </div>
      </div>
    </DialogHeaderSection>
  );
};

const updatePlayerSchema = z.object({
  firstName: z.string().min(1, { message: "Vorname ist erforderlich" }),
  lastName: z.string().min(1, { message: "Nachname ist erforderlich" }),
  jerseyNumber: z.number().optional(),
  birthDate: z.string().min(1, { message: "Geburtsdatum ist erforderlich" }),
  position: z.string().nullable().optional(),
  birthDateConsent: z.boolean(),
  nickname: z.string().optional(),
  nationality: z.string().optional(),
  biography: z.string().optional(),
  height: z.number().optional(),
  weight: z.number().optional(),
  belongsToTeamId: z.string().nullable().optional(),
  secondaryTeamIds: z.array(z.string()).optional(),
  image: z.instanceof(File).optional(),
});

type UpdatePlayerFormValues = z.infer<typeof updatePlayerSchema>;

const PlayerDetailsDialog = ({
  open,
  onClose,
  playerId,
  mode = "details",
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  playerId: string;
  mode?: DetailsBaseDialogMode;
}) => {
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { data: player, isLoading } = usePlayersFindOne(playerId, {
    enabled: open && !!playerId && !isDeleteInProgress,
  });
  const { mutate: deletePlayer, isPending: isDeleting } = usePlayersDelete();

  if (!player && !isLoading) {
    return (
      <DetailsBaseDialog
        open={open}
        onClose={onClose}
        icon={Users}
        title="Spieler-Details"
        description="Informationen zum Spieler einsehen und bearbeiten."
        deleteTitle="Spieler löschen"
        deleteItemTitle=""
        onDeleteConfirm={() => {}}
        detailsContent={<div className="py-12 text-center text-muted-foreground">Spieler nicht gefunden.</div>}
        editContent={() => null}
      />
    );
  }

  const handleDelete = async () => {
    if (!playerId) return;
    setIsDeleteInProgress(true);

    return new Promise<void>((resolve, reject) => {
      deletePlayer(
        { id: playerId },
        {
          onSuccess: () => {
            toast.success("Spieler wurde erfolgreich gelöscht");
            onClose(true);
            resolve();
          },
          onError: (error) => {
            toast.error("Spieler konnte nicht gelöscht werden", { error });
            setIsDeleteInProgress(false);
            reject(error);
          },
        }
      );
    });
  };

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      mode={mode}
      icon={Users}
      title="Spieler-Details"
      description="Informationen zum Spieler einsehen und bearbeiten."
      deleteTitle="Spieler löschen"
      deleteItemTitle={player ? `${player.firstName} ${player.lastName}` : "Lade..."}
      deleteItemTypeValue={playerId}
      onDeleteConfirm={handleDelete}
      isDeleting={isDeleting || isDeleteInProgress}
      isSaving={isSaving}
      canSave={canSave}
      formId="edit-player-form"
      detailsContent={isLoading ? <PlayerDetailsSkeleton /> : player ? <PlayerDetails player={player} /> : null}
      editContent={(onSaveSuccess) =>
        isLoading || !player ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <PlayerDetailsSkeleton />
          </div>
        ) : (
          <PlayerEditForm
            key={player._id}
            player={player}
            formId="edit-player-form"
            onSubmitting={setIsSaving}
            onValidityChange={setCanSave}
            onSaveSuccess={onSaveSuccess}
          />
        )
      }
    />
  );
};

const PlayerDetailsSkeleton = () => (
  <div className="space-y-6">
    <ManagedImageFieldSkeleton />

    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-2xl border bg-background p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-2 h-5 w-40" />
        </div>
      ))}
    </div>

    <div className="rounded-2xl border bg-background p-4">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="mt-2 h-5 w-64" />
      <Skeleton className="mt-4 h-3 w-28" />
      <div className="mt-2 flex gap-2">
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-32 rounded-full" />
      </div>
    </div>
  </div>
);

const PlayerDetails = ({ player }: { player: PlayerDetailsResponseDto }) => {
  const getSecondaryTeamNames = () =>
    (player.belongsToSecondaryTeamIds || [])
      .map((teamId) => player.selectableEntities.teams.find((team) => team._id === teamId)?.value)
      .filter(Boolean);

  const age = getAgeFromBirthDate(player.birthDate);
  const imageStates = player.imageStates?.length
    ? player.imageStates
    : player.imageState
      ? [{ _id: "primary", state: player.imageState }]
      : [];
  const imageCount = imageStates.length;
  const basicInfoItems: Array<{ label: string; value: React.ReactNode; icon: any }> = [
    {
      label: "Geburtsdatum",
      value: player.birthDate ? `${formatDateWithoutTime(player.birthDate)}${typeof age === "number" ? ` (${age} Jahre)` : ""}` : "-",
      icon: Calendar,
    },
    {
      label: "Nationalität",
      value: player.nationality || "-",
      icon: Flag,
    },
    {
      label: "Spitzname",
      value: player.nickname?.trim() ? player.nickname.trim() : "-",
      icon: Quote,
    },
    {
      label: "Größe & Gewicht",
      value: `${player.height ? `${player.height} cm` : "-"} / ${player.weight ? `${player.weight} kg` : "-"}`,
      icon: Scale,
    },
    {
      label: "Bilder",
      value: imageCount > 0 ? (
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-2">
            {imageStates.slice(0, 3).map((entry, index) => {
              const thumbnailUrl = entry.state.active?.thumbnailUrl || entry.state.active?.url;
              return (
                <span
                  key={entry._id}
                  className={cn(
                    "flex size-7 items-center justify-center overflow-hidden rounded-full border border-white bg-gray-100 shadow-sm",
                    index === 0 && "ring-1 ring-gray-200"
                  )}
                >
                  {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt="" className="h-full w-full object-cover object-top" />
                  ) : (
                    <Camera className="size-3.5 text-gray-400" />
                  )}
                </span>
              );
            })}
            {imageCount > 3 ? (
              <span className="flex size-7 items-center justify-center rounded-full border border-gray-200 bg-white text-[10px] font-semibold text-gray-600 shadow-sm">
                +{imageCount - 3}
              </span>
            ) : null}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-semibold text-gray-900">{imageCount} {imageCount === 1 ? "Bild" : "Bilder"}</span>
            <span className="text-xs font-medium text-gray-500">
              {imageCount > 1 ? "Mehrere gespeicherte Bilder" : "Ein gespeichertes Bild"}
            </span>
          </div>
        </div>
      ) : (
        "-"
      ),
      icon: ImageIcon,
    },
  ];

  return (
    <>
      <PlayerProfileHeader player={player} rightElement={null} />
      <DialogContentSection title="Informationen" icon={Radio}>
        <div className="grid gap-3 sm:grid-cols-2">
          {basicInfoItems.map((item) => (
            <DialogContentItem key={item.label} label={item.label} value={item.value} icon={item.icon} />
          ))}
        </div>
      </DialogContentSection>

      <DialogContentSection title="Teams" icon={Users}>
        <div className="space-y-4 rounded-xl border border-gray-200/60 bg-white p-4 sm:p-5">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Hauptmannschaft</span>
            <p className="text-sm font-semibold text-gray-900">{player.teamTitle || "-"}</p>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <span className="mb-2.5 block text-xs font-medium text-gray-500">Zusätzliche Mannschaften</span>
            <div className="flex flex-wrap gap-2">
              {getSecondaryTeamNames().length > 0 ? (
                getSecondaryTeamNames().map((teamName, index) => (
                  <Badge
                    key={`${teamName}-${index}`}
                    variant="secondary"
                    className="rounded-md border-transparent bg-gray-100 px-2.5 py-1 font-medium text-gray-700"
                  >
                    {teamName}
                  </Badge>
                ))
              ) : (
                <span className="text-sm font-semibold text-gray-400">-</span>
              )}
            </div>
          </div>
        </div>
      </DialogContentSection>

      {player.biography ? (
        <DialogTextSection title="Biographie" icon={BookOpen} text={formatTextWithLineBreaks(player.biography)} />
      ) : null}

      <DialogMetaSection createdAt={player.createdAt} updatedAt={player.updatedAt} />
    </>
  );
};

const PlayerEditForm = ({
  player,
  formId,
  onSubmitting,
  onValidityChange,
  onSaveSuccess,
}: {
  player: PlayerDetailsResponseDto;
  formId: string;
  onSubmitting: (isSubmitting: boolean) => void;
  onValidityChange: (isValid: boolean) => void;
  onSaveSuccess: () => void;
}) => {
  const { mutateAsync: updatePlayer } = usePlayersUpdate();
  const { mutateAsync: uploadImageAsset } = usePlayersUploadImageAsset();
  const { mutateAsync: deleteImageAsset } = usePlayersDeleteImageAsset();
  const { mutateAsync: applyImageAssetEdit } = usePlayersApplyImageAssetEdit();
  const { mutateAsync: previewBackgroundRemoval } = usePlayersPreviewBackgroundRemoval();
  const { mutateAsync: restoreOriginalImageAsset } = usePlayersRestoreOriginalImageAsset();
  const [mediaBusyAction, setMediaBusyAction] = useState<ManagedImageBusyAction>(null);
  const [busyImageId, setBusyImageId] = useState<string | null>(null);
  const [activeImageSelection, setActiveImageSelection] = useState<PlayerImageSelection | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    onSubmitting(isLoading || mediaBusyAction !== null);
  }, [isLoading, mediaBusyAction, onSubmitting]);

  const form = useForm<UpdatePlayerFormValues>({
    resolver: zodResolver(updatePlayerSchema),
    mode: "onChange",
    defaultValues: {
      firstName: player.firstName,
      lastName: player.lastName ?? "",
      jerseyNumber: player.jerseyNumber ?? undefined,
      birthDate: formatDateForForm(player.birthDate),
      position: player.position ?? null,
      birthDateConsent: player.birthDateConsent,
      nickname: player.nickname ?? "",
      nationality: player.nationality ?? "",
      biography: player.biography ?? "",
      height: player.height ?? undefined,
      weight: player.weight ?? undefined,
      belongsToTeamId: player.belongsToTeamId ?? "NONE",
      secondaryTeamIds: player.belongsToSecondaryTeamIds || [],
      image: undefined,
    },
  });

  const watchedValues = form.watch();
  const selectedImageFile = watchedValues.image;
  const existingImageStates = useMemo(
    () =>
      player.imageStates?.length
        ? player.imageStates
        : player.imageState
          ? [{ _id: "primary", state: player.imageState }]
          : [],
    [player.imageState, player.imageStates]
  );
  const selectedExistingImage = useMemo(
    () =>
      activeImageSelection?.type === "existing"
        ? existingImageStates.find((entry) => entry._id === activeImageSelection.id) ?? null
        : null,
    [activeImageSelection, existingImageStates]
  );
  const selectedExistingImageIndex = useMemo(
    () => (selectedExistingImage ? existingImageStates.findIndex((entry) => entry._id === selectedExistingImage._id) : -1),
    [existingImageStates, selectedExistingImage]
  );
  const newImagePreviewUrl = useMemo(() => {
    if (!selectedImageFile) return null;
    return URL.createObjectURL(selectedImageFile);
  }, [selectedImageFile]);

  const primaryTeamId = form.watch("belongsToTeamId");

  useEffect(() => {
    if (!newImagePreviewUrl?.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(newImagePreviewUrl);
  }, [newImagePreviewUrl]);

  useEffect(() => {
    if (!activeImageSelection) {
      setActiveImageSelection(existingImageStates[0] ? { type: "existing", id: existingImageStates[0]._id } : { type: "new" });
      return;
    }

    if (activeImageSelection.type === "existing") {
      const exists = existingImageStates.some((entry) => entry._id === activeImageSelection.id);
      if (!exists) {
        setActiveImageSelection(existingImageStates[0] ? { type: "existing", id: existingImageStates[0]._id } : { type: "new" });
      }
    }
  }, [activeImageSelection, existingImageStates]);

  const availableSecondaryTeams = useMemo(
    () =>
      parseTeamsToSelectOptions(
        primaryTeamId && primaryTeamId !== "NONE"
          ? player.selectableEntities.teams.filter((team) => team._id !== primaryTeamId)
          : player.selectableEntities.teams
      ),
    [player.selectableEntities.teams, primaryTeamId]
  );

  useEffect(() => {
    const selectedSecondaryTeamIds = form.getValues("secondaryTeamIds") ?? [];
    if (primaryTeamId && primaryTeamId !== "NONE" && selectedSecondaryTeamIds.includes(primaryTeamId)) {
      form.setValue(
        "secondaryTeamIds",
        selectedSecondaryTeamIds.filter((teamId) => teamId !== primaryTeamId),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  }, [form, primaryTeamId]);

  const hasChanges = useMemo(() => {
    const sameSecondaryTeams =
      JSON.stringify([...(player.belongsToSecondaryTeamIds || [])].sort()) ===
      JSON.stringify([...(watchedValues.secondaryTeamIds || [])].sort());

    return (
      watchedValues.firstName !== player.firstName ||
      (watchedValues.lastName ?? "") !== (player.lastName ?? "") ||
      (watchedValues.jerseyNumber ?? undefined) !== (player.jerseyNumber ?? undefined) ||
      watchedValues.birthDate !== formatDateForForm(player.birthDate) ||
      (watchedValues.position ?? null) !== (player.position ?? null) ||
      watchedValues.birthDateConsent !== player.birthDateConsent ||
      (watchedValues.nickname ?? "") !== (player.nickname ?? "") ||
      (watchedValues.nationality ?? "") !== (player.nationality ?? "") ||
      (watchedValues.biography ?? "") !== (player.biography ?? "") ||
      (watchedValues.height ?? undefined) !== (player.height ?? undefined) ||
      (watchedValues.weight ?? undefined) !== (player.weight ?? undefined) ||
      (watchedValues.belongsToTeamId ?? "NONE") !== (player.belongsToTeamId ?? "NONE") ||
      !sameSecondaryTeams ||
      !!watchedValues.image
    );
  }, [player, watchedValues]);

  useEffect(() => {
    onValidityChange(form.formState.isValid && hasChanges && mediaBusyAction === null);
  }, [form.formState.isValid, hasChanges, mediaBusyAction, onValidityChange]);

  const runMediaAction = async ({
    action,
    task,
    successMessage,
    errorMessage,
    imageId,
  }: {
    action: Exclude<ManagedImageBusyAction, null>;
    task: () => Promise<unknown>;
    successMessage: string;
    errorMessage: string;
    imageId?: string;
  }) => {
    setMediaBusyAction(action);
    setBusyImageId(imageId ?? null);

    try {
      await task();
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage, { error });
    } finally {
      setMediaBusyAction(null);
      setBusyImageId(null);
    }
  };

  const onSubmit = async (values: UpdatePlayerFormValues) => {
    setIsLoading(true);

    try {
      await updatePlayer({
        id: player._id,
        data: {
          firstName: values.firstName,
          lastName: values.lastName || undefined,
          jerseyNumber: values.jerseyNumber ?? undefined,
          birthDate: `${values.birthDate}T00:00:00.000Z`,
          position: values.position ?? null,
          birthDateConsent: values.birthDateConsent,
          nickname: values.nickname || undefined,
          nationality: values.nationality || undefined,
          biography: values.biography || undefined,
          height: values.height,
          weight: values.weight,
          belongsToTeamId: !values.belongsToTeamId || values.belongsToTeamId === "NONE" ? "NONE" : values.belongsToTeamId,
          secondaryTeamIds: values.secondaryTeamIds?.length ? values.secondaryTeamIds : [],
        },
      });

      if (values.image) {
        await persistManagedImageDraft({
          entityId: player._id,
          file: values.image,
          uploadImage: uploadImageAsset,
          applyImageEdit: ({ id: imageId, data }) =>
            applyImageAssetEdit({
              id: player._id,
              imageId,
              data,
            }),
          resolveEditEntityId: (uploadResponse) => uploadResponse._id,
        });
      }

      toast.success("Spieler wurde erfolgreich aktualisiert");
      onSaveSuccess();
    } catch (error) {
      toast.error("Spieler konnte nicht gespeichert werden", { error });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto p-4 sm:p-6">
          <DialogFormSection title="Spielerbild" icon={ImageIcon}>
            <div className="space-y-4">
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 py-1">
                {existingImageStates.map((imageEntry) => (
                  <PlayerImageGalleryItem
                    key={imageEntry._id}
                    previewUrl={imageEntry.state.active?.thumbnailUrl || imageEntry.state.active?.url}
                    isActive={activeImageSelection?.type === "existing" && activeImageSelection.id === imageEntry._id}
                    onClick={() => setActiveImageSelection({ type: "existing", id: imageEntry._id })}
                  />
                ))}
                <PlayerImageGalleryItem
                  previewUrl={newImagePreviewUrl}
                  isAddTile
                  isActive={activeImageSelection?.type === "new"}
                  onClick={() => setActiveImageSelection({ type: "new" })}
                />
              </div>

              {activeImageSelection?.type === "existing" && selectedExistingImage ? (
                <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-3 px-1">
                    <span className="text-sm font-semibold text-gray-900">Spielerbild {selectedExistingImageIndex + 1}</span>
                    <span className="rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200">
                      {selectedExistingImage.state.activeVariant === "edited" ? "Bearbeitet" : "Original"}
                    </span>
                  </div>
                  <ManagedImageField
                    value={selectedExistingImage.state}
                    onFileChange={() => undefined}
                    onDeleteRemote={() =>
                      runMediaAction({
                        action: "delete",
                        imageId: selectedExistingImage._id,
                        task: () => deleteImageAsset({ id: player._id, imageId: selectedExistingImage._id }),
                        successMessage: "Spielerbild wurde entfernt",
                        errorMessage: "Spielerbild konnte nicht entfernt werden",
                      })
                    }
                    onApplyEdit={(edit) =>
                      runMediaAction({
                        action: "applyEdit",
                        imageId: selectedExistingImage._id,
                        task: () => applyImageAssetEdit({ id: player._id, imageId: selectedExistingImage._id, data: edit }),
                        successMessage: "Bildbearbeitung wurde übernommen",
                        errorMessage: "Bild konnte nicht bearbeitet werden",
                      })
                    }
                    onRestoreOriginal={() =>
                      runMediaAction({
                        action: "restoreOriginal",
                        imageId: selectedExistingImage._id,
                        task: () => restoreOriginalImageAsset({ id: player._id, imageId: selectedExistingImage._id }),
                        successMessage: "Originalbild wurde wiederhergestellt",
                        errorMessage: "Originalbild konnte nicht wiederhergestellt werden",
                      })
                    }
                    enableBackgroundRemoval
                    busyAction={isLoading ? "saving" : busyImageId === selectedExistingImage._id ? mediaBusyAction : null}
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 bg-white p-3">
                  <ManagedImageField
                    file={selectedImageFile}
                    onFileChange={(nextFile) => form.setValue("image", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
                    enableBackgroundRemoval
                    busyAction={isLoading ? "saving" : mediaBusyAction}
                    emptyTitle="Weiteres Spielerbild auswählen"
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </div>
              )}
            </div>
          </DialogFormSection>

          <DialogFormSection title="Stammdaten" icon={IdCardLanyard}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Vorname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Max" {...field} className="h-11 bg-white" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Nachname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Mustermann" {...field} className="h-11 bg-white" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <Calendar className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Geburtsdatum
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} className="h-11 bg-white" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <MapPin className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Position
                    </FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                      value={field.value ?? "none"}
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 bg-white">
                          <SelectValue placeholder="Position wählen" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Keine Position</SelectItem>
                        {PLAYER_POSITIONS.map((playerPosition) => (
                          <SelectItem key={playerPosition.value} value={playerPosition.value}>
                            {playerPosition.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Teams" icon={Users}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="belongsToTeamId"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel isOptional={false}>
                      <Users className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Hauptmannschaft
                    </FormLabel>
                    <Select value={field.value ?? "NONE"} onValueChange={field.onChange} disabled={isLoading}>
                      <FormControl>
                        <SelectTrigger className="h-11 bg-white">
                          <SelectValue placeholder="Wähle die Hauptmannschaft" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">Keine Mannschaft</SelectItem>
                        {player.selectableEntities.teams.map((team) => (
                          <SelectItem key={team._id} value={team._id}>
                            {team.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="secondaryTeamIds"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      <Users className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Zusätzliche Mannschaften
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={availableSecondaryTeams}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Wähle zusätzliche Mannschaften"
                        className="py-1 bg-white min-h-[44px]"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Details" icon={LibraryBig}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="jerseyNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Hash className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Rückennummer
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="11"
                        {...field}
                        type="number"
                        className="h-11 bg-white"
                        disabled={isLoading}
                        onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nickname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Quote className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Spitzname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Max" {...field} className="h-11 bg-white" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Flag className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Nationalität
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Deutschland" {...field} className="h-11 bg-white" disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                <FormField
                  control={form.control}
                  name="height"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Scale className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                        Größe (cm)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="180"
                          {...field}
                          type="number"
                          className="h-11 bg-white"
                          disabled={isLoading}
                          onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Scale className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                        Gewicht (kg)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="80"
                          {...field}
                          type="number"
                          className="h-11 bg-white"
                          disabled={isLoading}
                          onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="biography"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      <BookOpen className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Biographie
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Erzähle etwas über den Spieler..."
                        className="min-h-[96px] resize-none bg-white"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDateConsent"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2 flex flex-row items-center gap-3 space-y-0 rounded-xl border border-gray-200 bg-white px-4 py-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} disabled={isLoading} />
                    </FormControl>
                    <div className="leading-tight">
                      <FormLabel>Geburtstags-Posts erlaubt?</FormLabel>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Wenn aktiv, können Geburtstagsposts automatisch erstellt werden.
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>
        </div>
      </form>
    </Form>
  );
};

export default PlayerDetailsDialog;
